import { useContext } from 'react';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCourseSummary,
  fetchLearnerHomeInit,
} from '@blend-ed/blendxplatform/api';

import messages from './messages';

const buildProgressByCourseId = (summary) => {
  if (!summary) return {};

  const candidates = [];
  if (Array.isArray(summary)) candidates.push(...summary);
  if (Array.isArray(summary?.courses)) candidates.push(...summary.courses);
  if (Array.isArray(summary?.course_summaries)) candidates.push(...summary.course_summaries);

  if (candidates.length === 0 && typeof summary === 'object') {
    const values = Object.values(summary);
    if (values.every((v) => typeof v === 'object' && v !== null)) {
      candidates.push(...values);
    }
  }

  return candidates.reduce((acc, item) => {
    const courseId = item?.course_id || item?.courseId || item?.id || item?.course_key;
    if (!courseId) return acc;

    const percent = item?.complete_percentage
      ?? item?.completion_percentage
      ?? item?.percent_complete
      ?? item?.progress_percent
      ?? item?.progressPercent;

    if (typeof percent === 'number' && !Number.isNaN(percent)) {
      acc[courseId] = percent;
    }

    return acc;
  }, {});
};

const startsInFuture = (startDateStr) => {
  if (!startDateStr) return false;
  const d = new Date(startDateStr);
  return !Number.isNaN(d.getTime()) && d > new Date();
};

const useOngoingCourses = () => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId || authenticatedUser?.id || authenticatedUser?.user_id || null;

  const { data: learnerHomeInit, isLoading: learnerHomeLoading } = useQuery({
    queryKey: ['learnerHomeInit'],
    queryFn: fetchLearnerHomeInit,
  });

  const { data: courseSummary, isLoading: courseSummaryLoading } = useQuery({
    queryKey: ['courseSummary', userId],
    queryFn: () => fetchCourseSummary(userId),
    enabled: !!userId,
  });

  const progressByCourseId = buildProgressByCourseId(courseSummary);
  const initCourses = learnerHomeInit?.courses || [];

  const ongoingItems = initCourses
    .filter((course) => !startsInFuture(course?.courseRun?.startDate))
    .map((course) => {
      const courseId = course?.courseRun?.courseId;
      const completionPercentFromInit = course?.completionData?.complete_percentage
        ?? (courseId ? learnerHomeInit?.completionData?.[courseId]?.complete_percentage : null);

      const progressPercent = (typeof completionPercentFromInit === 'number' && !Number.isNaN(completionPercentFromInit))
        ? Math.round(completionPercentFromInit)
        : ((courseId && progressByCourseId[courseId] != null) ? Math.round(progressByCourseId[courseId]) : 0);

      const certPreviewUrl = course?.certificate?.certPreviewUrl;
      return {
        id: courseId || course?.course?.courseName,
        courseId,
        title: course?.course?.courseName || intl.formatMessage(messages.fallbackCourseTitle),
        homeUrl: course?.courseRun?.homeUrl || null,
        progressPercent,
        imageUrl: `${getConfig().LMS_BASE_URL}${course.course.bannerImgSrc}` || null,
        certificateIsEarned: !!course?.certificate?.isEarned,
        certificateUrl: certPreviewUrl ? `${getConfig().LMS_BASE_URL}${certPreviewUrl}` : null,
      };
    })
    .filter((item) => !!item.id);

  const isLoading = learnerHomeLoading || (!!userId && courseSummaryLoading);

  return { ongoingItems, isLoading };
};

export default useOngoingCourses;
